import { useEffect, useState } from 'react';
import { getUsers, addStudentToClass, addToWaitlist } from '../../../services/adminService';
import { useAppContext } from '../../../context/AppContext';
import AddPassModal from '../users/AddPassModal';
import { matchesSearch } from '../../../utils/searchHelpers';

export default function AddStudentModal({ classId, classDetails, initialTab, onClose, onSuccess }) {
  const { setToaster, token } = useAppContext();
  const [search, setSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingUserId, setAddingUserId] = useState(null);
  /**
   * For offline / workshop / park classes: admin can add as "Online" (remote attendance on an in-person class).
   * Online-only classes (type === "online") are always booked as online — no toggle.
   */
  const [addAsOnline, setAddAsOnline] = useState(() => {
    if (initialTab === 'online') return true;
    return classDetails?.type === 'online';
  });
  const [addMode, setAddMode] = useState(() =>
    initialTab === 'waitlist' ? 'waitlist' : 'book'
  );

  // For adding pass to user without active pass
  const [showAddPass, setShowAddPass] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (classDetails?.type === 'online') {
      setAddAsOnline(true);
    } else if (initialTab !== 'online') {
      setAddAsOnline(false);
    }
  }, [classDetails?._id, classDetails?.type]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const usersData = await getUsers(token, 1, '', 1000, true); // includeBookings so we can sort by most bookings for this class type (group/individual/online)
      const users = usersData.users || [];
      setAllUsers(users);
      return users;
    } catch (error) {
      console.error('Error fetching data:', error);
      setToaster({
        message: "Failed to load data",
        type: "error",
        show: true,
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const classTypeIdOf = (v) => (v ? String(v?._id || v) : null);

  const getUserDisplayName = (user) => {
    const composed = `${user.given_name || ''} ${user.family_name || ''}`.trim();
    if (composed) return composed;
    return user.name || user.nickname || user.username || user.email || user.user_id || '';
  };

  const [addingToWaitlistId, setAddingToWaitlistId] = useState(null);

  const isAlreadyBookedForThisClass = (user, { onlyOffline = false } = {}) => {
    if (!Array.isArray(classDetails?.bookings)) return false;
    const userIdStr = String(user._id);
    return classDetails.bookings.some((b) => {
      if (b.status !== 'confirmed') return false;
      const bookingUserId = b.userId?._id || b.userId;
      if (String(bookingUserId) !== userIdStr) return false;
      return !onlyOffline || !b.online;
    });
  };

  const isAlreadyOnWaitlist = (user) => {
    if (!classDetails?.waitingList?.length) return false;
    return classDetails.waitingList.some((entry) => {
      const entryId = entry.userId?._id || entry.userId;
      return entryId && String(entryId) === String(user._id);
    });
  };

  const filteredUsers = allUsers.filter(user => {
    if (isAlreadyBookedForThisClass(user, { onlyOffline: addMode === 'waitlist' })) return false;

    const displayName = getUserDisplayName(user);
    return matchesSearch(search, [
      displayName,
      user.given_name,
      user.family_name,
      user.name,
      user.nickname,
      user.username,
      user.email,
      user.user_id,
    ]);
  });

  /** Display-only label for the class's type (sort/copy text), not used for eligibility gating. */
  const getClassCategory = () => {
    if (classDetails?.classType?.displayName) return classDetails.classType.displayName.toLowerCase();
    if (classDetails?.isIndividual) return 'individual';
    if (classDetails?.type === 'online') return 'online';
    return 'group';
  };

  const doesBookingMatchClassType = (booking) => {
    if (!booking || booking.status !== 'confirmed') return false;
    const classObj = booking.classId;
    if (!classObj || typeof classObj !== 'object') return false;

    const targetId = classTypeIdOf(classDetails?.classType);
    const bookingId = classTypeIdOf(classObj.classType);
    if (targetId != null && bookingId != null) return targetId === bookingId;

    // Fallback heuristic for legacy/unpopulated docs
    if (classDetails?.isIndividual) return classObj.isIndividual === true;
    if (classDetails?.type === 'online') return classObj.type === 'online';
    return classObj.isIndividual !== true && classObj.type !== 'online';
  };

  const getClassTypeBookingCount = (user) => {
    if (!Array.isArray(user?.bookings)) return 0;
    return user.bookings.reduce((count, booking) => (
      doesBookingMatchClassType(booking) ? count + 1 : count
    ), 0);
  };

  /** Confirmed bookings in this category where the user joined online (relevant when adding remote attendees to offline classes). */
  const getJoinOnlineBookingCount = (user) => {
    if (!Array.isArray(user?.bookings)) return 0;
    return user.bookings.reduce((count, booking) => {
      if (!doesBookingMatchClassType(booking) || booking.status !== 'confirmed' || !booking.online) {
        return count;
      }
      return count + 1;
    }, 0);
  };

  /** Mirrors server classService.isPassValidForLocation: allowedClassTypes are the real
   * in-person eligibility data (set via admin Pass Types matrix); classDetails.allowJoinOnline
   * is a property of the class, not the pass — any active pass may join online when set. */
  const isPassValidForClass = (pass, online) => {
    const passType = typeof pass.passTypeId === 'object' && pass.passTypeId ? pass.passTypeId : null;
    const allowedClassTypeIds = (passType?.allowedClassTypes || []).map(classTypeIdOf);
    const classTypeId = classTypeIdOf(classDetails?.classType);

    if (classDetails?.isIndividual === true) {
      return classTypeId != null && allowedClassTypeIds.includes(classTypeId);
    }

    const isOnlineClass = classDetails?.type === 'online';
    if ((isOnlineClass || classDetails?.allowJoinOnline === true) && (online || isOnlineClass)) {
      return true;
    }

    return classTypeId != null && allowedClassTypeIds.includes(classTypeId);
  };

  const isOnlineOnlyClass = classDetails?.type === 'online';
  /** True when booking as remote attendance (either online-only class, or join-online on an offline/park/workshop class). */
  const onlineForPassCheck = isOnlineOnlyClass ? true : addAsOnline;
  const canChooseInStudioOrOnline =
    !!classDetails && !classDetails.isIndividual && !isOnlineOnlyClass;

  const hasActivePass = (user) => {
    return user.passes?.some(pass => {
      if (pass.status !== 'active') return false;
      
      if (!isPassValidForClass(pass, onlineForPassCheck)) return false;
      
      // If unlimited classes (no classesIncluded), it's valid
      if (!pass.classesIncluded) return true;
      
      // Check if there are remaining classes
      return pass.classesUsed < pass.classesIncluded;
    });
  };

  // Get reason why user can't book
  const getPassIssue = (user) => {
    if (!user.passes?.length) return 'No pass';
    
    const activePasses = user.passes.filter(p => p.status === 'active');
    if (!activePasses.length) return 'No active pass';
    
    const validLocationPasses = activePasses.filter((p) => isPassValidForClass(p, onlineForPassCheck));
    if (!validLocationPasses.length) {
      const classAllowsOnlineJoin = isOnlineOnlyClass || classDetails?.allowJoinOnline === true;
      if (classAllowsOnlineJoin && !onlineForPassCheck) {
        return 'No pass valid in-person — switch to Online (any active pass can join online)';
      }

      if (onlineForPassCheck && !isOnlineOnlyClass) {
        return 'No pass valid for join-online on this class';
      }
      return `No pass for ${getClassCategory()} class`;
    }
    
    const hasRemainingClasses = validLocationPasses.some(p => 
      !p.classesIncluded || p.classesUsed < p.classesIncluded
    );
    if (!hasRemainingClasses) return 'Pass exhausted';
    
    return null;
  };

  const handleAddStudent = async (user) => {
    if (!hasActivePass(user)) {
      setSelectedUser(user);
      setShowAddPass(true);
      return;
    }

    await addStudentDirectly(user);
  };

  const addStudentDirectly = async (user) => {
    setAddingUserId(user.user_id);
    try {
      await addStudentToClass(token, classId, user.user_id, {
        online: onlineForPassCheck,
      });
      setToaster({
        message: `${user.name || user.email} added to class!`,
        type: "success",
        show: true,
      });
      onSuccess();
    } catch (error) {
      setToaster({
        message: error.response?.data?.error || "Failed to add student",
        type: "error",
        show: true,
      });
    } finally {
      setAddingUserId(null);
    }
  };

  const handleAddPassSuccess = async () => {
    setShowAddPass(false);
    const users = await fetchData();
    // Use the freshly-fetched list directly - `allUsers` state wouldn't be
    // updated yet inside this same closure.
    if (selectedUser) {
      const updatedUser = users.find(u => u.user_id === selectedUser.user_id);
      if (updatedUser && hasActivePass(updatedUser)) {
        await addStudentDirectly(updatedUser);
      }
    }
  };

  const handleAddPassClose = () => {
    setShowAddPass(false);
    setSelectedUser(null);
  };

  const handleAddToWaitlist = async (user) => {
    setAddingToWaitlistId(user.user_id);
    try {
      await addToWaitlist(token, classId, user._id);
      setToaster({
        message: `${user.name || user.email} added to waiting list!`,
        type: "success",
        show: true,
      });
      onSuccess();
    } catch (error) {
      setToaster({
        message: error.message || "Failed to add to waiting list",
        type: "error",
        show: true,
      });
    } finally {
      setAddingToWaitlistId(null);
    }
  };

  const sortedUsers = filteredUsers.slice().sort((a, b) => {
    const aClassTypeCount = getClassTypeBookingCount(a);
    const bClassTypeCount = getClassTypeBookingCount(b);
    if (bClassTypeCount !== aClassTypeCount) return bClassTypeCount - aClassTypeCount;

    // When adding remote attendance to an in-person class, prefer users who often join online
    if (onlineForPassCheck && !isOnlineOnlyClass) {
      const ao = getJoinOnlineBookingCount(a);
      const bo = getJoinOnlineBookingCount(b);
      if (bo !== ao) return bo - ao;
    }

    // Tiebreaker only: users with active pass first
    const aHasPass = hasActivePass(a) ? 1 : 0;
    const bHasPass = hasActivePass(b) ? 1 : 0;
    if (bHasPass !== aHasPass) return bHasPass - aHasPass;

    const aName = getUserDisplayName(a).toLowerCase();
    const bName = getUserDisplayName(b).toLowerCase();
    return aName.localeCompare(bName);
  });

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Add Student to Class</h3>
              {classDetails && (
                <>
                  <p className="text-xs text-gray-500">
                    {classDetails.title} • {classDetails.locationName || 'Studio'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {addMode === 'waitlist' ? 'Adding to waiting list' : `Listed by most ${getClassCategory()} bookings first`}
                  </p>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 w-full">
                {!isOnlineOnlyClass && (
                  <button
                    type="button"
                    onClick={() => { setAddMode('book'); setAddAsOnline(false); }}
                    className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                      addMode === 'book' && !addAsOnline
                        ? 'bg-white shadow text-brown'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Studio
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setAddMode('book'); setAddAsOnline(true); }}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    addMode === 'book' && (addAsOnline || isOnlineOnlyClass)
                      ? 'bg-white shadow text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Online
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode('waitlist')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    addMode === 'waitlist'
                      ? 'bg-white shadow text-amber-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Waitlist
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Search User</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full border border-gray-300 rounded p-2 text-base sm:text-sm"
                autoFocus
              />
            </div>

            {loading && (
              <p className="text-center text-sm text-gray-500">Loading users...</p>
            )}

            {!loading && sortedUsers.length > 0 && (
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
                {sortedUsers.map((user) => {
                  const hasPass = hasActivePass(user);
                  const passIssue = getPassIssue(user);
                  const isAddingThisUser = addingUserId === user.user_id;
                  return (
                    <div
                      key={user._id}
                      className="flex justify-between items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div>
                        <p className="font-semibold text-sm">
                          {getUserDisplayName(user)}
                          {getClassTypeBookingCount(user) > 0 && (
                            <span className="ml-1.5 text-gray-400 font-normal">
                              ({getClassTypeBookingCount(user)} {getClassCategory()} bookings)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-600">{user.email}</p>
                        {passIssue && (
                          <p className="text-xs text-yellow-600 mt-1">⚠️ {passIssue}</p>
                        )}
                      </div>
                      {addMode === 'waitlist' ? (
                        isAlreadyOnWaitlist(user) ? (
                          <span className="px-3 py-1 rounded text-xs bg-amber-50 text-amber-600">
                            On list
                          </span>
                        ) : (
                          <button
                            onClick={() => handleAddToWaitlist(user)}
                            disabled={!!addingToWaitlistId}
                            className="px-3 py-1 rounded text-xs disabled:opacity-50 bg-amber-100 text-amber-800 hover:bg-amber-200"
                          >
                            {addingToWaitlistId === user.user_id ? 'Adding...' : '+ Waitlist'}
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => handleAddStudent(user)}
                          disabled={!!addingUserId}
                          className={`px-3 py-1 rounded text-xs disabled:opacity-50 ${
                            hasPass 
                              ? 'bg-brown text-white hover:bg-pink' 
                              : 'bg-yellow-500 text-white hover:bg-yellow-600'
                          }`}
                        >
                          {isAddingThisUser ? 'Adding...' : hasPass ? 'Add' : 'Add Pass'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!loading && sortedUsers.length === 0 && (
              <p className="text-center text-sm text-gray-500">No users found</p>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add Pass Modal */}
      {showAddPass && selectedUser && (
        <AddPassModal
          userId={selectedUser.user_id}
          classDetails={classDetails}
          onClose={handleAddPassClose}
          onSuccess={handleAddPassSuccess}
        />
      )}
    </>
  );
}
